import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { Button, Grid, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import type { IContractToRegistrationProcessActivity } from '../../../classes/ContractToRegistrationProcessResponse';
import type { RegistrationStatuses } from '../../../services/rtkApi';
import { useExecuteTaskMutation } from '../../../services/rtkApi';
import { Loader } from '@klp/design/Loader';
import { Tooltip, Typography, Block } from '@klp/design';
import {
    handleRequiredFormFields,
    useMapUserRolesToCandidateGroup,
} from '../../../utils';
import { FormContainerKontraktsforvaltning } from '@klp/formgenerator/index';
import { contractregistrationSelectors } from '@klp/contractregistration/contractToRegistrationSlice';
import { showToast } from '@klpkonsern/ui';

type Statuses =
    | RegistrationStatuses.TIL_GODKJENNING
    | RegistrationStatuses.GODKJENT;

interface ConsiderReviewMeetingStepProps {
    handleCompleteStep: () => void;
    activity: IContractToRegistrationProcessActivity;

    status: {
        [key in Statuses]: string;
    };
    prosessInstansId: string;
}

const useStyles = makeStyles(() => ({
    buttonWrapper: {
        width: 'max-content',
        cursor: 'not-allowed',
    },
}));

export default function ConsiderReviewMeetingStep({
    handleCompleteStep,
    status,
    activity,
    prosessInstansId,
}: ConsiderReviewMeetingStepProps) {
    const { t } = useTranslation();
    const anchorReviewButtonRef = useRef<HTMLButtonElement>(null);
    const anchorContinueButtonRef = useRef<HTMLButtonElement>(null);
    const classes = useStyles();
    const { formFieldProcessing, formFieldError } = useSelector(
        contractregistrationSelectors,
    );

    const [
        executeTask,
        { isLoading: executeTaskLoading, isError: executeTaskError },
    ] = useExecuteTaskMutation();

    const { isUserCandidateForActivity } =
        useMapUserRolesToCandidateGroup(activity);

    const formFieldIsRequired = handleRequiredFormFields(activity?.formFields);

    const isLoading = executeTaskLoading || formFieldProcessing;

    const baseIsDisabled =
        activity.id === null ||
        activity.utfortDato !== null ||
        executeTaskLoading ||
        formFieldProcessing ||
        !isUserCandidateForActivity;

    const isDisabled = baseIsDisabled || formFieldError || formFieldIsRequired;

    const isDisabledWithoutFormError = baseIsDisabled;

    const handleComplete = (godkjentStatus: boolean) => {
        const taskId = activity?.id;

        if (!taskId || !status) {
            return;
        }

        if (handleCompleteStep) {
            handleCompleteStep();
        }

        executeTask({
            oppgaveId: taskId,
            registreringStatus: godkjentStatus
                ? status.Godkjent
                : status.TilGodkjenning,
        });
    };

    if (executeTaskError) {
        showToast({
            body: t('contractRegistration.error'),
            type: 'error',
        });
    }
    return (
        <>
            <Block paddingLeft="compact" paddingTop="tiny" paddingBottom="tiny">
                <Typography>
                    {t(
                        `checkOffContract.step.${activity?.taskDefinitionId}.description`,
                    )}
                </Typography>
            </Block>
            <Block paddingLeft="compact" paddingTop="tiny">
                {isLoading ? (
                    <Loader isLoading={isLoading} />
                ) : (
                    <>
                        {activity.formFields && (
                            <Block>
                                <FormContainerKontraktsforvaltning
                                    activity={activity}
                                    prosessInstansId={prosessInstansId}
                                    isDisabled={isDisabledWithoutFormError}
                                    completeTaskCallback={handleCompleteStep}
                                />
                            </Block>
                        )}

                        <Block>
                            {isDisabled ? (
                                <Block>
                                    <Grid
                                        container={true}
                                        direction="row"
                                        justifyContent="flex-start"
                                    >
                                        <Grid item={true}>
                                            <Tooltip
                                                translation="checkOffContract.tooltip"
                                                title=""
                                            >
                                                <Block
                                                    className={
                                                        classes.buttonWrapper
                                                    }
                                                >
                                                    <Button
                                                        disabled={isDisabled}
                                                        variant="outlined"
                                                        color="secondary"
                                                        onClick={() =>
                                                            handleComplete(
                                                                false,
                                                            )
                                                        }
                                                    >
                                                        {t(
                                                            `checkOffContract.step.${activity?.taskDefinitionId}.reviewMeetingButton`,
                                                        )}
                                                    </Button>
                                                </Block>
                                            </Tooltip>
                                        </Grid>
                                        <Grid item={true}>
                                            <Tooltip
                                                translation="checkOffContract.tooltip"
                                                title=""
                                            >
                                                <Block
                                                    className={
                                                        classes.buttonWrapper
                                                    }
                                                    paddingLeft="compact"
                                                >
                                                    <Button
                                                        disabled={isDisabled}
                                                        variant="contained"
                                                        color="secondary"
                                                        onClick={() =>
                                                            handleComplete(true)
                                                        }
                                                    >
                                                        {t(
                                                            `checkOffContract.step.${activity?.taskDefinitionId}.continueButton`,
                                                        )}
                                                    </Button>
                                                </Block>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                </Block>
                            ) : (
                                <Grid
                                    container={true}
                                    direction="row"
                                    justifyContent="flex-start"
                                >
                                    <Grid item={true}>
                                        <Block>
                                            <Button
                                                disabled={isLoading}
                                                ref={anchorReviewButtonRef}
                                                variant="outlined"
                                                color="secondary"
                                                onClick={() =>
                                                    handleComplete(false)
                                                }
                                            >
                                                {t(
                                                    `checkOffContract.step.${activity?.taskDefinitionId}.reviewMeetingButton`,
                                                )}
                                            </Button>
                                        </Block>
                                    </Grid>
                                    <Grid item={true}>
                                        <Block paddingLeft="compact">
                                            <Button
                                                disabled={isLoading}
                                                ref={anchorContinueButtonRef}
                                                variant="contained"
                                                color="secondary"
                                                onClick={() =>
                                                    handleComplete(true)
                                                }
                                            >
                                                {t(
                                                    `checkOffContract.step.${activity?.taskDefinitionId}.continueButton`,
                                                )}
                                            </Button>
                                        </Block>
                                    </Grid>
                                </Grid>
                            )}
                        </Block>
                    </>
                )}
            </Block>
        </>
    );
}
